import React, { useState, useEffect } from 'react';
import { Upload, X, Film, Save } from 'lucide-react';
import { toast } from 'react-toastify';
import { useNavigate, useParams } from 'react-router-dom';
import styles from './AddVideos.module.css'; // Reusing the same CSS module
import { useCategories } from '../../../hooks/categories/useCategories';
import { useSeriesByType } from '../../../hooks/useSeriesData';
import { useUpdateVideo, useVideo } from '../../../hooks/admin/useAdminVideoUpload';
import Loading from '../../../common/HousGlass Loader/Loading';


const EditVideo = () => {
  const { id } = useParams();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    mobileImage: null,
    computerImage: null,
    videoFile: null,
    category: '',
    language: 'english',
    duration: 0,
    isPublic: false,
    isActive: true,
    hasSeries: false,
    seriesId: '',
    chapterNumber: 1,
    removeMobileImage: false,
    removeComputerImage: false,
    meta_description: '',
    meta_keywords: '',
    page_title: '',
    slug: '',
  });

  const [mobileImagePreview, setMobileImagePreview] = useState('');
  const [computerImagePreview, setComputerImagePreview] = useState('');
  const [videoFileName, setVideoFileName] = useState('');
  const [originalVideo, setOriginalVideo] = useState(null);
  
  const { categories = [] } = useCategories('Video');
  const { series = [] } = useSeriesByType('Video');
  const { updateVideo, isLoading: isUpdating } = useUpdateVideo();
  const { video, isLoading: isVideoLoading, error: videoError } = useVideo(id);
  const navigate = useNavigate();

  // Initialize form with video data when loaded
  useEffect(() => {
    if (video) {
      setOriginalVideo(video);
      
      // Initialize form with existing data
      setFormData({
        title: video.title || '',
        description: video.description || '',
        category: video.category || '',
        language: video.language || 'english',
        duration: video.duration || 0,
        isPublic: video.is_public || false,
        isActive: video.is_active !== undefined ? video.is_active : true,
        hasSeries: video.have_series || false,
        seriesId: video.series_id || '',
        chapterNumber: video.chapter?.chapter_number || 1,
        removeMobileImage: false,
        removeComputerImage: false,
        meta_description: video.meta_description || '',
        meta_keywords: video.meta_keywords || '',
        page_title: video.page_title || '',
        slug: video.slug || '',
      });

      // Set image previews if they exist
      if (video.mobile_image_url) {
        setMobileImagePreview(video.mobile_image_url);
      }
      if (video.computer_image_url) {
        setComputerImagePreview(video.computer_image_url);
      }
      
      // Set video filename if it exists
      if (video.video_url) {
        const filename = video.video_url.split('/').pop();
        setVideoFileName(filename);
      }
    }
  }, [video]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleImageUpload = (e, type) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image size should be less than 5MB');
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        if (type === 'mobile') {
          setMobileImagePreview(reader.result);
          setFormData(prev => ({ 
            ...prev, 
            mobileImage: file,
            removeMobileImage: false
          }));
        } else {
          setComputerImagePreview(reader.result);
          setFormData(prev => ({ 
            ...prev, 
            computerImage: file,
            removeComputerImage: false
          }));
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleVideoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 1024 * 1024 * 1024) {
        toast.error('Video file size should be less than 1GB');
        return;
      }
      setVideoFileName(file.name);
      setFormData(prev => ({ ...prev, videoFile: file }));

      // Get video duration
      const video = document.createElement('video');
      video.src = URL.createObjectURL(file);
      video.onloadedmetadata = () => {
        setFormData(prev => ({
          ...prev,
          duration: Math.round(video.duration)
        }));
      };
    }
  };

  const removeImage = (type) => {
    if (type === 'mobile') {
      setMobileImagePreview('');
      setFormData(prev => ({ 
        ...prev, 
        mobileImage: null,
        removeMobileImage: originalVideo?.mobile_image_url ? true : false
      }));
    } else {
      setComputerImagePreview('');
      setFormData(prev => ({ 
        ...prev, 
        computerImage: null,
        removeComputerImage: originalVideo?.computer_image_url ? true : false
      }));
    }
  };

  const removeVideo = () => {
    setVideoFileName('');
    setFormData(prev => ({ ...prev, videoFile: null }));
  };

  const validateForm = () => {
    if (!formData.title.trim()) {
      toast.error('Please enter a title');
      return false;
    }
    
    if (!formData.category) {
      toast.error('Please select a category');
      return false;
    }
    
    if (formData.hasSeries) {
      if (!formData.seriesId) {
        toast.error('Please select a series');
        return false;
      }
      if (!formData.chapterNumber || formData.chapterNumber <= 0) {
        toast.error('Chapter number must be greater than 0');
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      await updateVideo({ 
        id, 
        formData 
      });
      // Navigate back to the previous page where the video list is displayed
      navigate(-1);
    } catch (error) {
      toast.error('Failed to update video');
    }
  };

  if (isVideoLoading || isUpdating) {
    return <div><Loading /></div>;
  }

  if (videoError) {
    return <div className={styles.errorMessage}>Error loading video: {videoError.message}</div>;
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Film className={styles.headerIcon} />
        <h1>Edit Video</h1>
      </div>

      <form onSubmit={handleSubmit} className={styles.form}>
        <div className={styles.formGroup}>
          <label htmlFor="title">Title</label>
          <input
            type="text"
            id="title"
            name="title"
            value={formData.title}
            onChange={handleInputChange}
            required
          />
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="category">Category</label>
          <select
            id="category"
            name="category"
            value={formData.category}
            onChange={handleInputChange}
            required
            className={styles.select}
          >
            <option value="">Select a category</option>
            {categories.map(category => (
              <option key={category.id} value={category.name}>
                {category.name}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="description">Description</label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            rows="4"
          />
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="language">Language</label>
          <select
            id="language"
            name="language"
            value={formData.language}
            onChange={handleInputChange}
            required
            className={styles.select}
          >
            <option value="english">English</option>
            <option value="hindi">Hindi</option>
            <option value="garhwali">Garhwali</option>
            <option value="kumaoni">Kumaoni</option>
            <option value="spanish">Spanish</option>
            <option value="french">French</option>
            <option value="german">German</option>
            <option value="chinese">Chinese</option>
            <option value="japanese">Japanese</option>
            <option value="korean">Korean</option>
            <option value="arabic">Arabic</option>
          </select>
        </div>

        {/* Series Options */}
        <div className={styles.formGroup}>
          <label className={styles.checkboxLabel}>
            <input
              type="checkbox"
              name="hasSeries"
              checked={formData.hasSeries}
              onChange={handleInputChange}
            />
            This video is part of a series
          </label>
        </div>

        {formData.hasSeries && (
          <>
            <div className={styles.formGroup}>
              <label htmlFor="seriesId">Select Series</label>
              <select
                id="seriesId"
                name="seriesId"
                value={formData.seriesId}
                onChange={handleInputChange}
                required={formData.hasSeries}
                className={styles.select}
              >
                <option value="">Select a series</option>
                {series.map(item => (
                  <option key={item.id} value={item.id}>
                    {item.title}
                  </option>
                ))}
              </select>
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="chapterNumber">Chapter Number</label>
              <input
                type="number"
                id="chapterNumber"
                name="chapterNumber"
                min="1"
                value={formData.chapterNumber}
                onChange={handleInputChange}
                required={formData.hasSeries}
              />
            </div>
          </>
        )}

        <div className={styles.imageUploadSection}>
          <div className={styles.imageUpload}>
            <label>Mobile Cover Image</label>
            <div className={styles.uploadArea}>
              {mobileImagePreview ? (
                <div className={styles.imagePreview}>
                  <img src={mobileImagePreview} alt="Mobile cover preview" />
                  <button
                    type="button"
                    onClick={() => removeImage('mobile')}
                    className={styles.removeButton}
                  >
                    <X />
                  </button>
                </div>
              ) : (
                <div className={styles.uploadPlaceholder}>
                  <input
                    type="file"
                    id="mobileImage"
                    accept="image/*"
                    onChange={(e) => handleImageUpload(e, 'mobile')}
                    className={styles.fileInput}
                  />
                  <Upload />
                  <span>Upload Mobile Cover</span>
                </div>
              )}
            </div>
          </div>

          <div className={styles.imageUpload}>
            <label>Computer Cover Image</label>
            <div className={styles.uploadArea}>
              {computerImagePreview ? (
                <div className={styles.imagePreview}>
                  <img src={computerImagePreview} alt="Computer cover preview" />
                  <button
                    type="button"
                    onClick={() => removeImage('computer')}
                    className={styles.removeButton}
                  >
                    <X />
                  </button>
                </div>
              ) : (
                <div className={styles.uploadPlaceholder}>
                  <input
                    type="file"
                    id="computerImage"
                    accept="image/*"
                    onChange={(e) => handleImageUpload(e, 'computer')}
                    className={styles.fileInput}
                  />
                  <Upload />
                  <span>Upload Computer Cover</span>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className={styles.videoUpload}>
          <label>Video File {originalVideo?.video_url && '(Leave empty to keep current video)'}</label>
          <div className={styles.uploadArea}>
            {videoFileName ? (
              <div className={styles.videoPreview}>
                <span>{videoFileName}</span>
                <button
                  type="button"
                  onClick={removeVideo}
                  className={styles.removeButton}
                >
                  <X />
                </button>
              </div>
            ) : (
              <div className={styles.uploadPlaceholder}>
                <input
                  type="file"
                  id="videoFile"
                  accept="video/*"
                  onChange={handleVideoUpload}
                  className={styles.fileInput}
                />
                <Upload />
                <span>Upload New Video File</span>
              </div>
            )}
          </div>
        </div>

        <div className={styles.formGroup}>
          <label className={styles.checkboxLabel}>
            <input
              type="checkbox"
              name="isPublic"
              checked={formData.isPublic}
              onChange={handleInputChange}
            />
            Make this video public
          </label>
        </div>

        <div className={styles.formGroup}>
          <label className={styles.checkboxLabel}>
            <input
              type="checkbox"
              name="isActive"
              checked={formData.isActive}
              onChange={handleInputChange}
            />
            Video is active
          </label>
        </div>

        {/* New SEO/Meta fields */}
        <div className={styles.formGroup}>
          <label htmlFor="slug">Slug</label>
          <input
            type="text"
            id="slug"
            name="slug"
            value={formData.slug}
            onChange={handleInputChange}
            placeholder="Auto-generated if left blank"
          />
        </div>
        <div className={styles.formGroup}>
          <label htmlFor="page_title">Page Title</label>
          <input
            type="text"
            id="page_title"
            name="page_title"
            value={formData.page_title}
            onChange={handleInputChange}
            placeholder="SEO Page Title"
          />
        </div>
        <div className={styles.formGroup}>
          <label htmlFor="meta_description">Meta Description</label>
          <textarea
            id="meta_description"
            name="meta_description"
            value={formData.meta_description}
            onChange={handleInputChange}
            rows="2"
            placeholder="SEO Meta Description"
          />
        </div>
        <div className={styles.formGroup}>
          <label htmlFor="meta_keywords">Meta Keywords</label>
          <input
            type="text"
            id="meta_keywords"
            name="meta_keywords"
            value={formData.meta_keywords}
            onChange={handleInputChange}
            placeholder="Comma-separated keywords"
          />
        </div>

        <button type="submit" className={styles.submitButton}>
          <Save className={styles.buttonIcon} />
          Update Video
        </button>
      </form>
    </div>
  );
};

export default EditVideo;